import type { FunctionComponent } from 'react';
import SidebarNavLayout from '../../layouts/SidebarNavLayout';
import SettingsPage from '../../shared/settings/SettingsPage';
import * as permissions from '../shared/permissions';
import type { RouterProps } from '../../../types';

const portalSettingPaths =
  Object.freeze(['/account/settings/organization/portal', '/account/settings/organization/portal/tokens']);

/**
 * OrganizationSettingsContainer is the top-level wrapper component for all the organization settings pages.
 */
const OrganizationSettingsContainer: FunctionComponent<RouterProps> = ({ children, location }) => {
  const { pathname } = location;
  return (
    <SidebarNavLayout>
      <SidebarNavLayout.Nav pathname={pathname}>
        <SidebarNavLayout.NavHeading>
          Organization
        </SidebarNavLayout.NavHeading>
        {permissions.shouldDisplayOrganizationGeneralSettings() && (
          <SidebarNavLayout.NavItem to="/account/settings/organization" activeKey="organization">
            General
          </SidebarNavLayout.NavItem>
        )}
        {permissions.shouldDisplayOrganizationPortalSettings() && (
          <SidebarNavLayout.NavItem
            to="/account/settings/organization/portal"
            isActive={portalSettingPaths.includes(pathname)}
          >
            Portal
          </SidebarNavLayout.NavItem>
        )}
        {/*  Users are always allowed to view the Teams page, so they can see which team(s) they are on. */}
        <SidebarNavLayout.NavItem
          to="/account/settings/organization/teams"
          //  We use the isActive prop instead of activeKey, because this route has
          //  sub-pages (like create/edit) which should also be considered active.
          isActive={pathname.startsWith('/account/settings/organization/teams')}
        >
          Teams
        </SidebarNavLayout.NavItem>
        {permissions.shouldDisplayUserSettings() && (
          <SidebarNavLayout.NavItem
            to="/account/settings/organization/roles"
            //  We use the isActive prop instead of activeKey, because this route has
            //  sub-pages (like create/edit) which should also be considered active.
            isActive={pathname.startsWith('/account/settings/organization/roles')}
          >
            Roles
          </SidebarNavLayout.NavItem>
        )}
        {permissions.shouldDisplayUserSettings() && (
          <SidebarNavLayout.NavItem
            to="/account/settings/organization/users"
            //  We use the isActive prop instead of activeKey, because this route has
            //  sub-pages (like create/edit) which should also be considered active.
            isActive={pathname.startsWith('/account/settings/organization/users')}
          >
            Users
          </SidebarNavLayout.NavItem>
        )}
        {permissions.shouldDisplayNotificationBanners() && (
          <SidebarNavLayout.NavItem to="/account/settings/organization/banners" activeKey="banners">
            Banners
          </SidebarNavLayout.NavItem>
        )}
        {permissions.shouldDisplayAuthenticationSettings() && (
          <SidebarNavLayout.NavItem to="/account/settings/organization/authentication" activeKey="authentication">
            Authentication
          </SidebarNavLayout.NavItem>
        )}
      </SidebarNavLayout.Nav>
      <SidebarNavLayout.Content>
        <SettingsPage>
          {children}
        </SettingsPage>
      </SidebarNavLayout.Content>
    </SidebarNavLayout>
  );
};

export default OrganizationSettingsContainer;
